import React, { ReactElement, useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { useAppSelector, getSubPath, UIGridBreakpoints, PanelState, useAppDispatch, GlobalConstants, PanelStateType } from "src/common";
import { DeveloperNavigationPathTypes } from "src/common/developers";
import { ErrorBoundary, RightSidebar, SplitPane, ToggleLeftNav } from "src/components";
import { DeveloperTopNavigation, DeveloperLeftNavigation } from "src/components/developer";
import { uiActions } from "src/store";
import { RootState } from "src/store/configureStore";

interface AllProps {
  children: ReactElement;
  className?: string;
  type: DeveloperNavigationPathTypes;
}

function WrapperComponent(props: AllProps) {
  const { children, className, type } = props;
  const current_location = useLocation();
  const dispatch = useAppDispatch();
  const left_nav_state = useAppSelector((state: RootState) => state.ui.left_nav_state);
  const [active_path, setActivePath] = useState<string>();
  const [active_sub_path, setActiveSubPath] = useState<string>();

  useEffect(() => {
    const path = getSubPath(current_location.pathname);
    const sub_path = getSubSubPath(current_location.pathname);
    !!path && setActivePath(path);
    !!sub_path ? setActiveSubPath(sub_path) : setActiveSubPath("");
    // NOTE: If left nav is opened and it is mobile, close it on navigation 
    (window.innerWidth <= UIGridBreakpoints.md && left_nav_state === PanelState.on) &&
      dispatch(uiActions.handleToggleLeftNavigation(PanelState.off));
  }, [current_location]);

  // Description: Trigger toggle the state of the Left Navigation
  // Only trigger toggle dispatch, if current left nav state is NOT equal new state 
  function toggleLeftNav(new_state: PanelStateType) {
    left_nav_state !== new_state && dispatch(uiActions.handleToggleLeftNavigation(new_state));
  }

  function getSubSubPath(_pathname: string): string | null {
    const arr = window.location.pathname.split("/");
    return arr.length > 3 ? arr[3] : null;
  }

  return (
    <ErrorBoundary>
      <>
        <DeveloperTopNavigation active_path={active_path} />
        <div className={`wrapper${!!className ? " " + className : ""}`}>
          <SplitPane
            className="wrapper-panels"
            minWidth={GlobalConstants.LEFT_NAV_MIN_WIDTH} // Left Nav Min width or closed width and also Min Width
            offWidth={GlobalConstants.LEFT_NAV_MIN_WIDTH}
            onWidth={GlobalConstants.LEFT_NAV_EXPANDED_WIDTH}  // Left Nav MAX Width or Right panel closed width window.innerWidth / 2
            toggleWidth={GlobalConstants.LEFT_NAV_TOGGLE_WIDTH}
            maxWidth={window.innerWidth / 2}
            panelState={left_nav_state}
            togglePanel={() => { toggleLeftNav(left_nav_state === PanelState.off ? PanelState.on : PanelState.off); }}
            left={
              <DeveloperLeftNavigation type={type} active_path={active_sub_path} handleAction={toggleLeftNav} />
            }
            right={
              <>
                <ToggleLeftNav
                  className="hide-desktop"
                  handleAction={() => { toggleLeftNav(left_nav_state === PanelState.off ? PanelState.on : PanelState.off); }} />
                <div className="content-wrapper">
                  {children}
                </div>
                <RightSidebar />
                <Tooltip
                  id="tb-tooltip"
                  disableStyleInjection="core"
                  closeEvents={{
                    mouseleave: true,
                    blur: true,
                    click: true,
                    dblclick: true,
                    mouseup: true,
                  }}
                />
              </>
            }
          />
        </div>
      </>
    </ErrorBoundary>
  );
}

export default React.memo(WrapperComponent);
